import {ApolloClient, ApolloLink, InMemoryCache} from '@apollo/client';
import {LocalState} from '@apollo/client/local-state';
import {BatchHttpLink} from '@apollo/client/link/batch-http';
import {FormattedExecutionResult} from 'graphql';
import {map} from 'rxjs';
import {fetchWithCsrf} from './customFetchers/fetchWithCsrf';
import {createRetryLink, RetryLinkConfig} from './helpers';
import {webflowDataIdFromObject} from './webflowDataIdFromObject';
import {
  createDynamoLoadMetrics,
  associateDynamoLoadMetrics,
  type DynamoLoadMetrics,
  type ProfilableFetch,
} from './dynamoLoadMetrics';

// Returns an ApolloClient instance
// https://www.apollographql.com/docs/react/
export const createApolloClient = ({
  origin = '',
  path,
  publicationId,
  previewKey,
  projectKey,
  ssrMode = false,
  credentials = 'same-origin',
  headers = {},
  useCsrf = false,
  retryConfig,
  onError,
  disableBatching = false,
  batchMax,
  customLinks = [],
  countDynamoLoadMetrics = false,
}: {
  origin?: string;
  path: string;
  previewKey?: string;
  projectKey?: string;
  publicationId?: string;
  ssrMode?: boolean;
  credentials?: RequestCredentials;
  useCsrf?: boolean;
  retryConfig?: RetryLinkConfig;
  onError?: (arg: FormattedExecutionResult) => void;
  headers?: Record<string, string>;
  disableBatching?: boolean;
  batchMax?: number;
  customLinks?: Array<ApolloLink>;
  // CONT-5981: opt-in for the always-on dynamo load counters (operations above
  // the batch link, HTTP requests below it). Enabled only on the two dynamo
  // clients; a no-op for every other Apollo client.
  countDynamoLoadMetrics?: boolean;
}) => {
  const uri = buildApolloClientUri({
    origin,
    path,
    publicationId,
    previewKey,
    projectKey,
  });

  const requestHeaders: Record<string, string> = {
    Accept: 'application/json',
    ...headers,
  };

  const batchLinkArgs: {
    uri: string;
    headers: Record<string, string>;
    credentials: RequestCredentials;
    batchMax?: number;
    batchInterval?: number;
    fetch?: ProfilableFetch;
  } = {
    uri,
    headers: requestHeaders,
    credentials,
  };

  // Disable batching
  if (disableBatching) {
    batchLinkArgs.batchMax = 1;
    batchLinkArgs.batchInterval = 0;
  } else if (typeof batchMax === 'number') {
    batchLinkArgs.batchMax = batchMax;
  }

  // CONT-5981 (always-on): a per-client set of dynamo load counters. State is
  // encapsulated in this instance (no module-level singleton), and it is
  // registered against the client below so consumers can look it up by client.
  const dynamoLoadMetrics: DynamoLoadMetrics | undefined =
    countDynamoLoadMetrics ? createDynamoLoadMetrics() : undefined;

  // Fetch using a CSRF token if toggled
  let fetcher: ProfilableFetch | undefined = useCsrf
    ? fetchWithCsrf
    : undefined;

  // CONT-5981 (always-on): count the HTTP requests below the batch link so the
  // realized batch effectiveness is visible in prod RUM. Increment-only; never
  // parses the body. No-op unless a load window is open.
  if (dynamoLoadMetrics) {
    fetcher = dynamoLoadMetrics.wrapFetch(fetcher);
  }

  if (fetcher) {
    batchLinkArgs.fetch = fetcher;
  }

  const batchLink = new BatchHttpLink(batchLinkArgs);

  const links: Array<ApolloLink> = [];

  if (retryConfig) {
    links.push(createRetryLink(retryConfig));
  }

  if (onError) {
    const errorMapperLink = new ApolloLink((operation, forward) => {
      return forward(operation).pipe(
        map((response) => {
          if (response.errors) {
            onError(response);
          }
          return response;
        })
      );
    });

    links.push(errorMapperLink);
  }

  // CONT-5981 (always-on): count dynamo GraphQL operations above the batch link.
  // Prepended so it observes every operation before batching coalesces them.
  const effectiveCustomLinks = dynamoLoadMetrics
    ? [dynamoLoadMetrics.countingLink, ...customLinks]
    : customLinks;

  const apolloClient = new ApolloClient({
    link: ApolloLink.from([...effectiveCustomLinks, ...links, batchLink]),
    cache: new InMemoryCache({
      dataIdFromObject: webflowDataIdFromObject,
    }),
    localState: new LocalState(),
    ssrMode,
  });

  // Register the counters against the client so the Designer shell bridge (and
  // any other consumer) can retrieve them with `getDynamoLoadMetrics(client)`.
  if (dynamoLoadMetrics) {
    associateDynamoLoadMetrics(apolloClient, dynamoLoadMetrics);
  }

  return apolloClient;
};

export const buildApolloClientUri = ({
  origin = '',
  path,
  publicationId,
  previewKey,
  projectKey,
}: {
  origin?: string;
  path: string;
  publicationId?: string;
  previewKey?: string;
  projectKey?: string;
}) => {
  const params: Array<any | string> = [];
  if (publicationId) {
    params.push(`pub=${publicationId}`);
  }
  if (previewKey) {
    params.push(`preview=${previewKey}`);
  }
  if (projectKey) {
    params.push(`project=${projectKey}`);
  }
  // Replace multiple slashes with single slashes
  const cleanPath = `${origin}${path}`.replace(/([^:])\/\/+/g, '$1/');
  return `${cleanPath}${params.length ? `?${params.join('&')}` : ''}`;
};
